// RequireAuth Route Guard
// LazySEM Frontend - Blocks protected subtree until auth is resolved.
//
// Fixes the pre-P0 flash where Layout mounted its children before its
// useEffect could redirect unauthenticated users to /login. Any authenticated
// queries fired during that window are now prevented from running at all.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface RequireAuthProps {
  children: ReactNode;
}

const RequireAuth = ({ children }: RequireAuthProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--color-bg-primary, #fff)',
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid var(--color-border, #e5e7eb)',
            borderTopColor: 'var(--color-primary, #3b82f6)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default RequireAuth;
